import * as React from "react";
import { useState, useRef } from "react";
import { Typography, Paper, Button } from "@material-ui/core";
import { Help as HelpIcon, OpenInNew as OpenInNewIcon } from "@material-ui/icons";
import UploadNewRxToS3Section from "./sections/UploadNewRxToS3Section";
import RetrieveRxFromS3Section from "./sections/RetrieveRxFromS3Section";
import IngestNewRxToUPMSSection from "./sections/IngestNewRxToUPMSSection";
import IngestTransferRxToUPMSSection from "./sections/IngestTransferRxToUPMSSection";
import UnifiedNewRxWorkflowSection from "./sections/UnifiedNewRxWorkflowSection";
import UnifiedTransferRxWorkflowSection from "./sections/UnifiedTransferRxWorkflowSection";
import UnifiedRenewalWorkflowSection from "./sections/UnifiedRenewalWorkflowSection";
import RxChangeResponseSection from "./sections/RxChangeResponseSection";
import ApprovePrescriptionStepSection from "./sections/ApprovePrescriptionStepSection";
import RouteToFollowUpStepSection from "./sections/RouteToFollowUpStepSection";
import CreateOrderStepSection from "./sections/CreateOrderStepSection";
import { SidebarNavigation } from "./components";
import { COLORS, SPACING } from "./styles/theme";
import { LAYOUT_CONFIG } from "./config/layoutConfig";
import { INewRxStepHandoff } from "./types/PrescriptionIngestionTypes";

enum PrescriptionIngestionOperation {
  NONE,
  UNIFIED_NEWRX_WORKFLOW,
  UNIFIED_TRANSFERRX_WORKFLOW,
  UPLOAD_NEWRX_TO_S3,
  RETRIEVE_RX_FROM_S3,
  INGEST_NEWRX_TO_UPMS,
  INGEST_TRANSFERRX_TO_UPMS,
  APPROVE_PRESCRIPTION,
  ROUTE_TO_FOLLOW_UP,
  ROUTE_TRANSFERRX_TO_FOLLOW_UP,
  CREATE_ORDER,
  UNIFIED_RENEWAL_WORKFLOW,
  RX_CHANGE_RESPONSE,
}

interface IPrescriptionIngestionSectionProps {
  // Add props here if needed in the future
}

const PrescriptionIngestionSection: React.FC<
  IPrescriptionIngestionSectionProps
> = () => {
  const [currentOperation, setCurrentOperation] =
    useState<PrescriptionIngestionOperation>(
      PrescriptionIngestionOperation.NONE
    );
  // Context carried by the "Continue" buttons across the NewRx Operations steps.
  // Cleared when an operation is chosen from the sidebar directly, so a directly
  // visited step asks the user for the values it needs instead of using stale data.
  const [handoff, setHandoff] = useState<INewRxStepHandoff | undefined>(
    undefined
  );

  const operationContentRef = useRef<HTMLDivElement>(null);

  const selectOperation = (operation: PrescriptionIngestionOperation) => {
    setHandoff(undefined);
    setCurrentOperation(operation);
  };

  // Advance to another step carrying handoff context (used by Continue buttons).
  const goToStep = (
    operation: PrescriptionIngestionOperation,
    nextHandoff: INewRxStepHandoff
  ) => {
    setHandoff(nextHandoff);
    setCurrentOperation(operation);
  };

  // Convert enum to string for SidebarNavigation component
  const currentOperationString =
    PrescriptionIngestionOperation[currentOperation];

  return (
    <div
      style={{
        display: "flex",
        minHeight: `calc(100vh - ${LAYOUT_CONFIG.TOP_NAV_HEIGHT}px)`,
        paddingTop: LAYOUT_CONFIG.TOP_SPACING,
      }}
    >
      {/* Sidebar Navigation */}
      <SidebarNavigation
        currentOperation={currentOperationString}
        onOperationSelect={(operation) =>
          selectOperation(
            PrescriptionIngestionOperation[
              operation as keyof typeof PrescriptionIngestionOperation
            ]
          )
        }
      />

      {/* Main Content Area */}
      <div
        style={{
          marginLeft: LAYOUT_CONFIG.SIDEBAR_WIDTH - 200,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: `calc(100vh - ${LAYOUT_CONFIG.TOP_NAV_HEIGHT}px)`,
          backgroundColor: COLORS.background,
        }}
      >
        {/* Fixed Header Section */}
        <div
          style={{
            backgroundColor: COLORS.white,
            paddingTop: 24,
            paddingBottom: 24,
            paddingLeft: SPACING.large,
            paddingRight: SPACING.large,
            borderBottom: `2px solid ${COLORS.gray200}`,
            flexShrink: 0,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 4,
                  height: 40,
                  backgroundColor: "#ff9800",
                  borderRadius: 2,
                }}
              />
              <div>
                <Typography
                  variant="h4"
                  style={{
                    color: "#232f3e",
                    fontWeight: 600,
                    marginBottom: 4,
                    fontSize: "24px",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Prescription Ingestion Management
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: "#718096",
                    fontSize: "14px",
                  }}
                >
                  Ingest NewRx, TransferRx, Renewal and RxChangeResponse messages, then approve, route to follow-up, or order
                </Typography>
              </div>
            </div>
            
            {/* Tutorial Link */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<HelpIcon />}
              endIcon={<OpenInNewIcon />}
              onClick={() => window.open('https://w.amazon.com/bin/view/Pharmacy/Public/Teams/APAQ/Automation/TestTools/Portal/Prescriptions/APAQPortalPrescriptionIngestionHow-toguideNewRxTransferRx#HUnifiedNewRxWorkflow', '_blank')}
              style={{
                borderColor: '#ff9800',
                color: '#ff9800',
                textTransform: 'none',
                fontWeight: 500,
                padding: '6px 16px',
                borderRadius: '6px',
                fontSize: '13px',
              }}
            >
              How-to Guide
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          style={{
            flexGrow: 1,
            overflowY: "auto",
            paddingTop: SPACING.large,
            paddingBottom: SPACING.large,
            paddingLeft: SPACING.large,
            paddingRight: SPACING.large,
          }}
        >
          {/* Content Sections */}
          <div ref={operationContentRef}>
            {currentOperation ===
              PrescriptionIngestionOperation.UNIFIED_NEWRX_WORKFLOW && (
              <UnifiedNewRxWorkflowSection key="unified-newrx" />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.UNIFIED_TRANSFERRX_WORKFLOW && (
              <UnifiedTransferRxWorkflowSection key="unified-transferrx" />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.UNIFIED_RENEWAL_WORKFLOW && (
              <UnifiedRenewalWorkflowSection key="unified-renewal" />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.RX_CHANGE_RESPONSE && (
              <RxChangeResponseSection key="rx-change-response" />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.UPLOAD_NEWRX_TO_S3 && (
              <UploadNewRxToS3Section
                key="upload-s3"
                onContinue={(next) =>
                  goToStep(
                    PrescriptionIngestionOperation.INGEST_NEWRX_TO_UPMS,
                    next
                  )
                }
              />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.RETRIEVE_RX_FROM_S3 && (
              <RetrieveRxFromS3Section key="retrieve-s3" />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.INGEST_NEWRX_TO_UPMS && (
              <IngestNewRxToUPMSSection
                key="ingest-newrx"
                handoff={handoff}
                onContinue={(next) =>
                  goToStep(
                    PrescriptionIngestionOperation.APPROVE_PRESCRIPTION,
                    next
                  )
                }
                onContinueToFollowUp={(next) =>
                  goToStep(
                    PrescriptionIngestionOperation.ROUTE_TO_FOLLOW_UP,
                    next
                  )
                }
              />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.ROUTE_TO_FOLLOW_UP && (
              <RouteToFollowUpStepSection key="route-to-follow-up" handoff={handoff} />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.INGEST_TRANSFERRX_TO_UPMS && (
              <IngestTransferRxToUPMSSection key="ingest-transferrx" />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.ROUTE_TRANSFERRX_TO_FOLLOW_UP && (
              <RouteToFollowUpStepSection key="route-transferrx-to-follow-up" flow="transferRx" />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.APPROVE_PRESCRIPTION && (
              <ApprovePrescriptionStepSection
                key="approve-prescription"
                handoff={handoff}
                onContinue={(next) =>
                  goToStep(
                    PrescriptionIngestionOperation.CREATE_ORDER,
                    next
                  )
                }
              />
            )}

            {currentOperation ===
              PrescriptionIngestionOperation.CREATE_ORDER && (
              <CreateOrderStepSection key="create-order" handoff={handoff} />
            )}

            {currentOperation === PrescriptionIngestionOperation.NONE && (
              <Paper
                style={{
                  padding: SPACING.xlarge,
                  textAlign: "center",
                  backgroundColor: COLORS.white,
                }}
              >
                <Typography
                  variant="h6"
                  style={{ color: COLORS.textSecondary }}
                >
                  Select an operation from the sidebar to get started
                </Typography>
              </Paper>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionIngestionSection;
